import axios, {
    AxiosResponse,
    AxiosRequestConfig
} from "axios";
import { AuthFactory } from "./auth";
import { useImpersonatorStore } from "@/shared/store/impersonator";
import { useNotifierStore } from "@/shared/store/notifier";
import { useUserStore } from "@/shared/store/user";

export const authorizedAxios = {
    delete<T> (url: string, params?: any) {
        return authorizedAxios.request<T>({ url, method: "DELETE", params });
    },
    get<T> (url: string, params?: any) {
        return authorizedAxios.request<T>({ url, method: "GET", params });
    },
    put<T> (url: string, data?: any) {
        return authorizedAxios.request<T>({ url, method: "PUT", data });
    },
    post<T> (url: string, data?: any) {
        return authorizedAxios.request<T>({ url, method: "POST", data });
    },
    postFile<T> (url: string, data?: any) {
        return authorizedAxios.request<T>({ url, method: "POST", responseType: "arraybuffer", data });
    },
    getFile<T> (url: string) {
        return authorizedAxios.request<T>({
            url,
            method: "GET",
            responseType: "blob"
        });
    },
    postFiles<T> (url: string, files: any[], params?: any) {
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            formData.append(`files[${i}]`, file);
        }
        return authorizedAxios.request<T>({
            url,
            method: "POST",
            data: formData,
            headers: {
                "Content-Type": "multipart/form-data"
            },
            params
        });
    },
    request<T> (cfg: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        const impersonatorStore = useImpersonatorStore();
        const notifierStore = useNotifierStore();
        const userStore = useUserStore();
        return new Promise<AxiosResponse<T>>((resolve, reject) => {
            AuthFactory.getAuth().accessToken()
                .then(token => {
                    if (impersonatorStore.impersonationIsActive
                        && impersonatorStore.impersonationToken) {
                        return {
                            ...cfg, headers: {
                                Authorization: impersonatorStore.impersonationToken,
                                ...cfg.headers
                            }
                        };
                    }
                    if (token) {
                        return { ...cfg, headers: { Authorization: token, ...cfg.headers } };
                    }
                    return cfg;
                })
                .then(cfg => axios.request(cfg))
                .then(response => resolve(response))
                .catch(err => {
                    if (err && err.response && err.response.status === 401) {
                        if (impersonatorStore.impersonationIsActive && err.response.config.method.toLowerCase() !== "get") {
                            notifierStore.setDanger("Modifying user data is not allowed while impersonating a user!", notifierStore.shortTimeout);
                            return;
                        }
                        userStore.logout();
                        return;
                    }

                    reject(err);
                });
        });
    }
};